import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import {
  Code2,
  Smartphone,
  Cloud,
  Lightbulb,
  Users,
  Shield,
  ChevronRight,
  Mail,
  Phone,
  MapPin,
  Star,
  ArrowRight,
  Check,
  Menu,
  X,
  Moon,
  Sun,
  MessageCircle,
  Send,
  Minimize2,
  XCircle,
} from "lucide-react";
import { createBrowserRouter } from "react-router-dom";
import SuccessPage from "./components/SuccessPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [visibleProjects, setVisibleProjects] = useState(4);
  const [visibleTestimonials, setVisibleTestimonials] = useState(3);
  const [isDarkMode, setIsDarkMode] = useState(true); // Dark mode by default
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatMessage, setChatMessage] = useState("");
  const [chatMessages, setChatMessages] = useState([
    {
      type: "bot",
      message:
        "Hello! 👋 Welcome to Threem Technology Solutions. How can we help you today?",
      timestamp: new Date(),
    },
  ]);
  const [selectedService, setSelectedService] = useState(null);

  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.remove("light-mode");
      document.documentElement.classList.remove("light-mode");
      const inputs = document.querySelectorAll("input, textarea");

      inputs.forEach((el) => {
        el.style.color = "white";
        el.style.backgroundColor = "black";
      });
    } else {
      document.body.classList.add("light-mode");
      document.documentElement.classList.add("light-mode");
      const inputs = document.querySelectorAll("input, textarea");

      inputs.forEach((el) => {
        el.style.color = "black";
        el.style.backgroundColor = "white";
      });
    }
  }, [isDarkMode]);

  const handleChatSubmit = (e) => {
    e.preventDefault();
    if (!chatMessage.trim()) return;

    // Add user message
    const userMsg = {
      type: "user",
      message: chatMessage,
      timestamp: new Date(),
    };
    setChatMessages((prev) => [...prev, userMsg]);
    setChatMessage("");

    // Auto-reply with helpful message
    setTimeout(() => {
      const botMsg = {
        type: "bot",
        message:
          "Thank you for your message! Our Threem Technology Solutions team will get back to you shortly. For immediate assistance, please call us at +91 9902545635 or email Info@threemsolutions.com",
        timestamp: new Date(),
      };
      setChatMessages((prev) => [...prev, botMsg]);
    }, 1000);
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "home",
        "services",
        "about",
        "portfolio",
        "testimonials",
        "contact",
      ];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    debugger;
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setIsMenuOpen(false);
      history.pushState(null, "", `#${sectionId}`);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll get back to you soon.");
      // setFormData({
      //   name: "",
      //   email: "",
      //   phone: "",
      //   company: "",
      //   message: "",
      // });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();

    try {
      await axios.post(`${API}/newsletter`, { email: newsletterEmail });
      toast.success("Successfully subscribed to newsletter!");
      setNewsletterEmail("");
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("Email already subscribed");
      } else {
        toast.error("Failed to subscribe. Please try again.");
      }
    }
  };

  const services = [
    {
      icon: <Code2 className="w-12 h-12" />,
      title: "Microsoft Development",
      description:
        "Leverage the power of .NET framework to build robust, scalable, and secure web applications with full-stack expertise.",
      details: {
        overview:
          "Our Microsoft development expertise spans the entire .NET ecosystem, delivering enterprise-grade solutions that scale with your business. We specialize in building robust, maintainable applications using the latest Microsoft technologies.",
        features: [
          "ASP.NET Core web applications and APIs",
          "Azure cloud integration and deployment",
          "SQL Server database design and optimization",
          "Entity Framework and LINQ for data access",
          "Blazor for interactive web UIs",
          "Microservices architecture with .NET",
        ],
        technologies: [
          ".NET Core",
          "ASP.NET",
          "Azure",
          "SQL Server",
          "C#",
          "Blazor",
          "Entity Framework",
        ],
        benefits: [
          "Enterprise-grade security and scalability",
          "Seamless integration with Microsoft ecosystem",
          "High performance and reliability",
          "Long-term support and stability",
        ],
      },
    },
    {
      icon: <Code2 className="w-12 h-12" />,
      title: "Angular & React Development",
      description:
        "Create dynamic and responsive web applications with exceptional user experiences using modern frameworks.",
      details: {
        overview:
          "We build modern, responsive web applications using Angular and React - the industry's leading frontend frameworks. Our developers create intuitive user interfaces that deliver exceptional user experiences across all devices.",
        features: [
          "Single Page Applications (SPAs)",
          "Progressive Web Apps (PWAs)",
          "Real-time data synchronization",
          "Component-based architecture",
          "State management (Redux, NgRx)",
          "Responsive and mobile-first design",
        ],
        technologies: [
          "React.js",
          "Angular",
          "TypeScript",
          "Redux",
          "Next.js",
          "Material-UI",
          "Tailwind CSS",
        ],
        benefits: [
          "Fast, interactive user experiences",
          "Reusable component architecture",
          "SEO-friendly applications",
          "Cross-platform compatibility",
        ],
      },
    },
    {
      icon: <Lightbulb className="w-12 h-12" />,
      title: "IT Consulting",
      description:
        "Strategic guidance to navigate technology adoption, optimize IT infrastructure, and implement best practices.",
      details: {
        overview:
          "Our IT consulting services help organizations make informed technology decisions, optimize their IT infrastructure, and implement industry best practices. We provide strategic guidance tailored to your business objectives.",
        features: [
          "Technology assessment and roadmap planning",
          "Digital transformation strategy",
          "Infrastructure optimization",
          "Cloud migration consulting",
          "Security audit and compliance",
          "Process automation recommendations",
        ],
        technologies: [
          "Cloud Platforms",
          "DevOps Tools",
          "Security Solutions",
          "Analytics Tools",
          "Automation Frameworks",
        ],
        benefits: [
          "Reduced IT costs and complexity",
          "Improved operational efficiency",
          "Risk mitigation and compliance",
          "Technology-driven competitive advantage",
        ],
      },
    },
    {
      icon: <Smartphone className="w-12 h-12" />,
      title: "Mobile Application Development",
      description:
        "Build native and cross-platform mobile apps focusing on functionality, UX, and platform optimization.",
      details: {
        overview:
          "We develop high-performance mobile applications for iOS and Android platforms. Whether you need native apps or cross-platform solutions, we deliver mobile experiences that engage users and drive business results.",
        features: [
          "Native iOS and Android development",
          "Cross-platform with React Native/Flutter",
          "Offline functionality and data sync",
          "Push notifications and real-time updates",
          "Payment gateway integration",
          "App Store optimization and deployment",
        ],
        technologies: [
          "React Native",
          "Flutter",
          "Swift",
          "Kotlin",
          "Firebase",
          "Redux",
          "Native APIs",
        ],
        benefits: [
          "Reach wider audience on mobile platforms",
          "Native performance and user experience",
          "Faster time-to-market with cross-platform",
          "Seamless backend integration",
        ],
      },
    },
    {
      icon: <Cloud className="w-12 h-12" />,
      title: "Custom Software Development",
      description:
        "Tailored software solutions designed specifically to meet your business needs and enhance operational efficiency.",
      details: {
        overview:
          "We design and develop custom software solutions that address your unique business challenges. Our agile approach ensures solutions that fit perfectly with your workflows and scale with your growth.",
        features: [
          "Requirements analysis and planning",
          "Custom business logic implementation",
          "Third-party system integration",
          "Workflow automation",
          "Reporting and analytics dashboards",
          "Scalable architecture design",
        ],
        technologies: [
          "Python",
          "Node.js",
          "Java",
          "PostgreSQL",
          "MongoDB",
          "Redis",
          "Docker",
          "Kubernetes",
        ],
        benefits: [
          "Solutions tailored to your exact needs",
          "Competitive advantage through custom features",
          "Improved efficiency and productivity",
          "Full ownership and control",
        ],
      },
    },
    {
      icon: <Shield className="w-12 h-12" />,
      title: "Technical Support",
      description:
        "Comprehensive assistance with troubleshooting, maintenance, and optimization to ensure smooth operations.",
      details: {
        overview:
          "Our technical support services ensure your systems run smoothly 24/7. We provide proactive monitoring, rapid issue resolution, and continuous optimization to minimize downtime and maximize performance.",
        features: [
          "24/7 monitoring and support",
          "Incident management and resolution",
          "Performance optimization",
          "Security updates and patches",
          "Backup and disaster recovery",
          "Documentation and training",
        ],
        technologies: [
          "Monitoring Tools",
          "Ticketing Systems",
          "Remote Support",
          "Backup Solutions",
          "Security Tools",
        ],
        benefits: [
          "Minimized downtime and disruptions",
          "Proactive issue prevention",
          "Rapid response to critical issues",
          "Peace of mind for your business",
        ],
      },
    },
  ];

  const testimonials = [
    {
      name: "Rajesh Kumar",
      company: "TechCorp India",
      rating: 5,
      text: "Despite being a new company, Threem Technology Solutions delivered an outstanding ERP system. The founders' years of experience really showed in their technical expertise and professional approach.",
    },
    {
      name: "Priya Sharma",
      company: "Digital Ventures",
      rating: 5,
      text: "The mobile app they developed exceeded our expectations. Professional team, timely delivery, and excellent post-launch support.",
    },
    {
      name: "Amit Patel",
      company: "Finance Solutions Ltd",
      rating: 5,
      text: "The team's deep industry experience was evident from day one. Their consulting services helped us modernize our entire IT infrastructure with great ROI.",
    },
    {
      name: "Meera Reddy",
      company: "HealthTech Solutions",
      rating: 5,
      text: "Working with Threem was a game-changer for our healthcare platform. They understood our needs perfectly and delivered a robust solution.",
    },
    {
      name: "Vikram Singh",
      company: "E-Commerce Hub",
      rating: 5,
      text: "Outstanding React development services! Our online store now handles 10x more traffic with zero downtime. Highly recommended.",
    },
    {
      name: "Ananya Krishnan",
      company: "Retail Chain Pvt Ltd",
      rating: 5,
      text: "The custom software they built streamlined our entire supply chain. Excellent communication throughout the project lifecycle.",
    },
    {
      name: "Suresh Iyer",
      company: "Manufacturing Co.",
      rating: 5,
      text: "Threem's technical support is exceptional. They're always available when we need them and resolve issues promptly.",
    },
    {
      name: "Kavita Desai",
      company: "Financial Services Inc",
      rating: 5,
      text: "Their Angular expertise helped us build a secure, scalable banking platform. Professional team with deep technical knowledge.",
    },
    {
      name: "Arjun Malhotra",
      company: "Logistics Solutions",
      rating: 5,
      text: "From consultation to deployment, the entire process was smooth. They truly understand enterprise-level requirements.",
    },
  ];

  const portfolio = [
    {
      title: "Enterprise Resource Planning System",
      category: "Microsoft Development",
      technologies: ["ASP.NET Core", "SQL Server", "Azure"],
      description: "Comprehensive ERP solution for manufacturing industry",
      image:
        "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80",
    },
    {
      title: "E-Commerce Platform",
      category: "React Development",
      technologies: ["React", "Node.js", "MongoDB"],
      description: "Scalable online marketplace with real-time inventory",
      image:
        "https://images.unsplash.com/photo-1557821552-17105176677c?w=800&q=80",
    },
    {
      title: "Healthcare Mobile App",
      category: "Mobile Development",
      technologies: ["React Native", "Firebase", "Redux"],
      description: "Patient management and telemedicine platform",
      image:
        "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&q=80",
    },
    {
      title: "Banking Dashboard",
      category: "Custom Software",
      technologies: ["Angular", "Spring Boot", "PostgreSQL"],
      description: "Real-time analytics and reporting for financial services",
      image:
        "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80",
    },
    {
      title: "Supply Chain Management System",
      category: "Microsoft Development",
      technologies: [".NET Core", "Azure", "Power BI"],
      description: "End-to-end logistics tracking and warehouse management",
      image:
        "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&q=80",
    },
    {
      title: "Restaurant POS System",
      category: "Custom Software",
      technologies: ["React", "Python", "MySQL"],
      description: "Complete point-of-sale with inventory and billing",
      image:
        "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80",
    },
    {
      title: "Real Estate Portal",
      category: "React Development",
      technologies: ["React", "GraphQL", "PostgreSQL"],
      description: "Property listing platform with virtual tours",
      image:
        "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80",
    },
    {
      title: "Fitness Tracking App",
      category: "Mobile Development",
      technologies: ["Flutter", "Firebase", "TensorFlow"],
      description: "AI-powered workout and nutrition tracking mobile app",
      image:
        "https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=800&q=80",
    },
    {
      title: "HR Management Portal",
      category: "Angular Development",
      technologies: ["Angular", "Java", "Oracle"],
      description: "Complete HRMS with payroll and attendance",
      image:
        "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=80",
    },
    {
      title: "IoT Dashboard for Smart Homes",
      category: "Custom Software",
      technologies: ["React", "MQTT", "InfluxDB"],
      description: "Real-time monitoring and control of smart devices",
      image:
        "https://images.unsplash.com/photo-1558002038-1055907df827?w=800&q=80",
    },
    {
      title: "Education Learning Platform",
      category: "React Development",
      technologies: ["React", "Node.js", "WebRTC"],
      description: "Online learning with live classes and assessments",
      image:
        "https://images.unsplash.com/photo-1501504905252-473c47e087f8?w=800&q=80",
    },
    {
      title: "Fleet Management System",
      category: "Microsoft Development",
      technologies: [".NET", "GPS APIs", "SQL Server"],
      description: "Vehicle tracking with route optimization",
      image:
        "https://images.unsplash.com/photo-1519003722824-194d4455a60c?w=800&q=80",
    },
  ];

  return (
    <div className="App">
      {/* Navigation */}
      <nav className="nav-bar">
        <div className="nav-container">
          <div className="nav-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_better-experience-2/artifacts/xs82axsg_Logo.jpg"
              alt="Threem Solutions Logo"
              className="logo-image"
            />
            <span className="logo-text">Threem Technology Solutions</span>
          </div>

          <div className={`nav-links ${isMenuOpen ? "active" : ""}`}>
            {[
              "home",
              "services",
              "about",
              "portfolio",
              "testimonials",
              "contact",
            ].map((item) => (
              <button
                key={item}
                data-testid={`nav-${item}`}
                className={`nav-link ${activeSection === item ? "active" : ""}`}
                onClick={() => scrollToSection(item)}
              >
                {item.charAt(0).toUpperCase() + item.slice(1)}
              </button>
            ))}
          </div>

          <Button
            data-testid="nav-contact-btn"
            className="nav-cta-btn"
            onClick={() => scrollToSection("contact")}
          >
            Get Started
          </Button>

          <button
            className="theme-switcher"
            onClick={() => setIsDarkMode(!isDarkMode)}
            data-testid="theme-toggle-btn"
            title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
          >
            {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
          </button>

          <button
            data-testid="mobile-menu-toggle"
            className="mobile-menu-btn"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero-section">
        <video autoPlay loop muted playsInline className="hero-video-bg">
          <source
            src="https://customer-assets.emergentagent.com/job_better-experience-2/artifacts/6gaeav4i_Title%20Intro.mp4"
            type="video/mp4"
          />
        </video>
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="hero-text">
            <div className="hero-badge" data-testid="hero-badge">
              <span className="badge-dot"></span>
              Leading Technology Solutions Provider
            </div>
            <h1 className="hero-title" data-testid="hero-title">
              Simplifying Technology,
              <br />
              <span className="hero-title-gradient">
                Maximizing Business Potential
              </span>
            </h1>
            <p className="hero-description" data-testid="hero-description">
              We build custom software solutions to streamline operations, boost
              performance, and reduce risks. Transform your business with
              cutting-edge technology.
            </p>
            <div className="hero-buttons">
              <Button
                data-testid="hero-get-started-btn"
                size="lg"
                className="hero-primary-btn"
                onClick={() => scrollToSection("contact")}
              >
                Get Started Today
                <ArrowRight className="ml-2" size={20} />
              </Button>
              <Button
                data-testid="hero-learn-more-btn"
                size="lg"
                variant="outline"
                className="hero-secondary-btn"
                onClick={() => scrollToSection("services")}
              >
                Discover More
              </Button>
            </div>
            <div className="hero-stats">
              <div className="stat-item">
                <div className="stat-number">15+</div>
                <div className="stat-label">Years Founders' Experience</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">50+</div>
                <div className="stat-label">Projects Delivered</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">98%</div>
                <div className="stat-label">Client Satisfaction</div>
              </div>
            </div>
          </div>
          <div className="hero-visual">
            <div className="floating-card card-1">
              <Code2 size={32} />
              <span>Full-Stack Development</span>
            </div>
            <div className="floating-card card-2">
              <Smartphone size={32} />
              <span>Mobile Solutions</span>
            </div>
            <div className="floating-card card-3">
              <Cloud size={32} />
              <span>Cloud Integration</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title" data-testid="services-title">
              Our Services
            </h2>
            <p className="section-subtitle">
              Domain knowledge, talent and years of experience
            </p>
          </div>
          <div className="services-grid">
            {services.map((service, index) => (
              <Card
                key={index}
                className="service-card"
                data-testid={`service-card-${index}`}
              >
                <CardContent className="service-card-content">
                  <div className="service-icon">{service.icon}</div>
                  <h3 className="service-title">{service.title}</h3>
                  <p className="service-description">{service.description}</p>
                  <button
                    className="service-learn-more"
                    onClick={() => setSelectedService(service)}
                    data-testid={`service-learn-more-${index}`}
                  >
                    Learn More <ChevronRight size={16} />
                  </button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Service Details Modal */}
      {selectedService && (
        <div
          className="service-modal-overlay"
          onClick={() => setSelectedService(null)}
          data-testid="service-modal-overlay"
        >
          <div
            className="service-modal"
            onClick={(e) => e.stopPropagation()}
            data-testid="service-modal"
          >
            <button
              className="modal-close-btn"
              onClick={() => setSelectedService(null)}
              data-testid="modal-close-btn"
            >
              <XCircle size={24} />
            </button>

            <div className="modal-header">
              <div className="modal-icon">{selectedService.icon}</div>
              <h2 className="modal-title">{selectedService.title}</h2>
            </div>

            <div className="modal-content">
              <div className="modal-section">
                <h3 className="modal-section-title">Overview</h3>
                <p className="modal-text">{selectedService.details.overview}</p>
              </div>

              <div className="modal-section">
                <h3 className="modal-section-title">Key Features</h3>
                <ul className="modal-list">
                  {selectedService.details.features.map((feature, idx) => (
                    <li key={idx} className="modal-list-item">
                      <Check size={18} className="list-icon" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="modal-section">
                <h3 className="modal-section-title">Technologies</h3>
                <div className="modal-tags">
                  {selectedService.details.technologies.map((tech, idx) => (
                    <span key={idx} className="modal-tag">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              <div className="modal-section">
                <h3 className="modal-section-title">Benefits</h3>
                <ul className="modal-list">
                  {selectedService.details.benefits.map((benefit, idx) => (
                    <li key={idx} className="modal-list-item">
                      <Check size={18} className="list-icon" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="modal-actions">
                <Button
                  className="modal-contact-btn"
                  onClick={() => {
                    setSelectedService(null);
                    scrollToSection("contact");
                  }}
                  data-testid="modal-contact-btn"
                >
                  Get Started
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* About Section */}
      <section id="about" className="about-section">
        <div className="section-container">
          <div className="about-grid">
            <div className="about-content">
              <h2 className="section-title" data-testid="about-title">
                About Threem Technology Solutions
              </h2>
              <p className="about-text">
                Founded on January 19, 2024, Threem Technology Solutions is a
                dynamic startup built on a foundation of deep IT expertise. Our
                founders bring over 15+ years of industry experience, combining
                their knowledge to deliver innovative software solutions that
                drive business growth and operational excellence across diverse
                industries.
              </p>
              <p className="about-text">
                Despite being a young company, we leverage our founders'
                extensive background in Microsoft technologies, modern web
                frameworks, mobile development, and cloud solutions. We don't
                just write code – we craft solutions that solve real business
                problems and deliver measurable value, backed by years of proven
                expertise.
              </p>
              <div className="about-features">
                <div className="about-feature">
                  <Check className="feature-icon" />
                  <span>Industry-leading expertise</span>
                </div>
                <div className="about-feature">
                  <Check className="feature-icon" />
                  <span>Agile development methodology</span>
                </div>
                <div className="about-feature">
                  <Check className="feature-icon" />
                  <span>24/7 technical support</span>
                </div>
                <div className="about-feature">
                  <Check className="feature-icon" />
                  <span>Scalable & secure solutions</span>
                </div>
              </div>
            </div>
            <div className="about-image">
              <img
                src="https://customer-assets.emergentagent.com/job_better-experience-2/artifacts/fd2stl58_THREEM%20a.png"
                alt="Threem Solutions"
                className="about-img about-branded-img"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="portfolio-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title" data-testid="portfolio-title">
              Our Portfolio
            </h2>
            <p className="section-subtitle">
              Successful projects that showcase our expertise
            </p>
          </div>
          <div className="portfolio-grid">
            {portfolio.slice(0, visibleProjects).map((project, index) => (
              <Card
                key={index}
                className="portfolio-card"
                data-testid={`portfolio-card-${index}`}
              >
                <div className="portfolio-image-wrapper">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="portfolio-image"
                  />
                  <div className="portfolio-overlay">
                    <div className="portfolio-category">{project.category}</div>
                  </div>
                </div>
                <CardContent className="portfolio-card-content">
                  <h3 className="portfolio-title">{project.title}</h3>
                  <p className="portfolio-description">{project.description}</p>
                  <div className="portfolio-tech">
                    {project.technologies.map((tech, idx) => (
                      <span key={idx} className="tech-tag">
                        {tech}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          {visibleProjects < portfolio.length && (
            <div className="load-more-container">
              <Button
                data-testid="portfolio-load-more-btn"
                onClick={() => setVisibleProjects((prev) => prev + 4)}
                className="load-more-btn"
                size="lg"
              >
                Load More Projects
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Our Story Section */}
      <section className="story-section">
        <div className="section-container">
          <div className="story-content">
            <div className="story-text-wrapper">
              <div className="story-badge">Est. January 19, 2024</div>
              <h2 className="section-title" data-testid="story-title">
                A New Company Built on
                <br />
                Proven Expertise
              </h2>
              <p className="story-text">
                Threem Technology Solutions was founded by seasoned IT
                professionals with a collective experience of over 15 years in
                the technology industry. Our founders have worked with Fortune
                500 companies, led development teams, and delivered solutions
                across diverse sectors including finance, healthcare, retail,
                and manufacturing.
              </p>
              <p className="story-text">
                While our company is young, our expertise is not. We bring
                battle-tested knowledge in enterprise architecture, agile
                methodologies, and cutting-edge technologies to every project.
                Our mission is to provide startup agility with enterprise-level
                quality.
              </p>
              <div className="story-highlights">
                <div className="story-highlight-item">
                  <div className="highlight-number">2024</div>
                  <div className="highlight-label">Company Founded</div>
                </div>
                <div className="story-highlight-item">
                  <div className="highlight-number">15+</div>
                  <div className="highlight-label">
                    Years Collective Experience
                  </div>
                </div>
                <div className="story-highlight-item">
                  <div className="highlight-number">50+</div>
                  <div className="highlight-label">Successful Projects</div>
                </div>
              </div>
            </div>
            <div className="story-visual">
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80"
                alt="Team collaboration"
                className="story-img"
              />
              <div className="story-card">
                <h4>Our Vision</h4>
                <p>
                  To be the trusted technology partner for businesses seeking
                  innovative, reliable, and scalable software solutions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title" data-testid="why-choose-title">
              Why Choose Threem Technology Solutions
            </h2>
            <p className="section-subtitle">Experience that drives success</p>
          </div>
          <div className="why-choose-wrapper">
            <div className="why-choose-grid">
              <div className="why-choose-card">
                <div className="why-choose-icon">
                  <Users size={32} />
                </div>
                <h3 className="why-choose-stat">15+</h3>
                <p className="why-choose-label">Years Founders' Experience</p>
                <p className="why-choose-text">
                  Seasoned professionals with deep industry expertise
                </p>
              </div>
              <div className="why-choose-card">
                <div className="why-choose-icon">
                  <Shield size={32} />
                </div>
                <h3 className="why-choose-stat">100%</h3>
                <p className="why-choose-label">Secure Solutions</p>
                <p className="why-choose-text">
                  Enterprise-grade security in every project
                </p>
              </div>
              <div className="why-choose-card">
                <div className="why-choose-icon">
                  <Code2 size={32} />
                </div>
                <h3 className="why-choose-stat">50+</h3>
                <p className="why-choose-label">Projects Delivered</p>
                <p className="why-choose-text">
                  Successfully delivered since our founding
                </p>
              </div>
              <div className="why-choose-card">
                <div className="why-choose-icon">
                  <Lightbulb size={32} />
                </div>
                <h3 className="why-choose-stat">24/7</h3>
                <p className="why-choose-label">Support Available</p>
                <p className="why-choose-text">
                  Dedicated team ready to assist anytime
                </p>
              </div>
            </div>
            <div className="why-choose-brand">
              <img
                src="https://customer-assets.emergentagent.com/job_better-experience-2/artifacts/fd2stl58_THREEM%20a.png"
                alt="Threem Solutions Brand"
                className="why-choose-brand-img"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="testimonials-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title" data-testid="testimonials-title">
              Client Testimonials
            </h2>
            <p className="section-subtitle">What our clients say about us</p>
          </div>
          <div className="testimonials-grid">
            {testimonials
              .slice(0, visibleTestimonials)
              .map((testimonial, index) => (
                <Card
                  key={index}
                  className="testimonial-card"
                  data-testid={`testimonial-card-${index}`}
                >
                  <CardContent className="testimonial-content">
                    <div className="testimonial-stars">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} size={20} fill="currentColor" />
                      ))}
                    </div>
                    <p className="testimonial-text">"{testimonial.text}"</p>
                    <div className="testimonial-author">
                      <div className="author-avatar">
                        {testimonial.name.charAt(0)}
                      </div>
                      <div className="author-info">
                        <div className="author-name">{testimonial.name}</div>
                        <div className="author-company">
                          {testimonial.company}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
          </div>
          {visibleTestimonials < testimonials.length && (
            <div className="load-more-container">
              <Button
                data-testid="testimonials-load-more-btn"
                onClick={() => setVisibleTestimonials((prev) => prev + 3)}
                className="load-more-btn"
                size="lg"
              >
                Load More Testimonials
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title" data-testid="contact-title">
              Get In Touch
            </h2>
            <p className="section-subtitle">
              Let's discuss how we can help your business grow
            </p>
          </div>
          <div className="contact-grid">
            <div className="contact-info">
              <h3 className="contact-info-title">Contact Information</h3>
              <div className="contact-info-list">
                <div className="contact-info-item">
                  <MapPin className="contact-icon" />
                  <div>
                    <div className="contact-label">Address</div>
                    <div className="contact-value">
                      Unit 101, Oxford Towers 139
                      <br />
                      HAL Old Airport Rd, HAL II Stage
                      <br />
                      Bangalore, Karnataka - 560008
                    </div>
                  </div>
                </div>
                <div className="contact-info-item">
                  <Phone className="contact-icon" />
                  <div>
                    <div className="contact-label">Phone</div>
                    <div className="contact-value">+91 9902545635</div>
                  </div>
                </div>
                <div className="contact-info-item">
                  <Mail className="contact-icon" />
                  <div>
                    <div className="contact-label">Email</div>
                    <div className="contact-value">
                      Info@threemsolutions.com
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <Card className="contact-form-card">
              <CardContent className="contact-form-content">
                <form action="https://api.web3forms.com/submit" method="POST">
                  <Input
                    type="hidden"
                    name="access_key"
                    value={process.env.REACT_APP_WEBFORM_KEY}
                  />
                  <div className="form-group">
                    <Input
                      data-testid="contact-name-input"
                      placeholder="Your Name *"
                      pattern="[A-Za-z\s]+"
                      title="name can only contain letters"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      required
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <Input
                      data-testid="contact-email-input"
                      type="email"
                      placeholder="Email Address *"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      required
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <Input
                      data-testid="contact-phone-input"
                      placeholder="Phone Number"
                      pattern="\d+"
                      title="Phone number can only contain digits"
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData({ ...formData, phone: e.target.value })
                      }
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <Input
                      data-testid="contact-company-input"
                      placeholder="Company Name"
                      pattern="[A-Za-z\s]+"
                      title="Company name can only contain letters"
                      value={formData.company}
                      onChange={(e) =>
                        setFormData({ ...formData, company: e.target.value })
                      }
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <Textarea
                      data-testid="contact-message-input"
                      placeholder="Your Message *"
                      value={formData.message}
                      onChange={(e) =>
                        setFormData({ ...formData, message: e.target.value })
                      }
                      required
                      rows={5}
                      className="form-input"
                    />
                  </div>

                  <div>
                    <Input
                      type="hidden"
                      name="details"
                      value={`You have received a new message from ${
                        formData.name
                      }.

Company: ${formData.company || "N/A"}
Email: ${formData.email}
Phone: ${formData.phone || "N/A"}

Message:
${formData.message}
`}
                    />
                    <Input
                      type="hidden"
                      name="subject"
                      value={`New inquiry from ${formData.name}`}
                    />
                    {
                      <input
                        type="hidden"
                        name="redirect"
                        value={`${window.location.origin}/threem-ui/success`}
                      />
                    }

                    <Input
                      type="hidden"
                      name="replyto"
                      value={formData.email}
                    />

                    <Input
                      type="hidden"
                      name="from_name"
                      value={formData.name}
                    />
                  </div>

                  <Button
                    data-testid="contact-submit-btn"
                    type="submit"
                    className="form-submit-btn"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Sending..." : "Send Message"}
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Chat Widget */}
      <div
        className={`
        chat-widget ${isChatOpen ? "open" : ""}`}
      >
        {!isChatOpen ? (
          <button
            className={`chat-toggle-btn`}
            onClick={() => setIsChatOpen(true)}
            data-testid="chat-toggle-btn"
          >
            <MessageCircle size={24} />
            <span className={`chat-badge }`}>Chat with us</span>
          </button>
        ) : (
          <div className="chat-window" data-testid="chat-window">
            <div className="chat-header">
              <div className="chat-header-content">
                <MessageCircle size={20} />
                <div>
                  <div className="chat-title">Threem Technology Solutions</div>
                  <div className="chat-status">
                    <span className="status-dot"></span>
                    Online
                  </div>
                </div>
              </div>
              <button
                className="chat-close-btn"
                onClick={() => setIsChatOpen(false)}
                data-testid="chat-close-btn"
              >
                <Minimize2 size={18} />
              </button>
            </div>
            <div className="chat-messages">
              {chatMessages.map((msg, index) => (
                <div
                  key={index}
                  className={`chat-message ${msg.type}`}
                  data-testid={`chat-message-${msg.type}`}
                >
                  <div className={isDarkMode ? "bg-black" : "message-content"}>
                    {msg.message}{" "}
                  </div>
                  <div className="message-time">
                    {msg.timestamp.toLocaleTimeString("en-US", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                </div>
              ))}
            </div>
            <form className="chat-input-form" onSubmit={handleChatSubmit}>
              <Input
                data-testid="chat-input"
                placeholder="Type your message..."
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                className="chat-input"
              />
              <Button
                type="submit"
                className="chat-send-btn"
                data-testid="chat-send-btn"
              >
                <Send size={18} />
              </Button>
            </form>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-grid">
            <div className="footer-col">
              <div className="footer-logo">
                <img
                  src="https://customer-assets.emergentagent.com/job_better-experience-2/artifacts/xs82axsg_Logo.jpg"
                  alt="Threem Solutions Logo"
                  className="logo-image"
                />
                <span className="logo-text">Threem Technology Solutions</span>
              </div>
              <p className="footer-description">
                Building innovative software solutions that drive business
                growth and operational excellence.
              </p>
              <form
                onSubmit={handleNewsletterSubmit}
                className="newsletter-form"
                data-testid="newsletter-form"
              >
                <Input
                  data-testid="newsletter-email-input"
                  type="email"
                  placeholder="Subscribe to newsletter"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  required
                  className="newsletter-input"
                />
                <Button
                  data-testid="newsletter-submit-btn"
                  type="submit"
                  size="sm"
                  className="newsletter-btn"
                >
                  Subscribe
                </Button>
              </form>
            </div>
            <div className="footer-col">
              <h4 className="footer-title">Services</h4>
              <ul className="footer-links">
                <li>
                  <a href="#services">Microsoft Development</a>
                </li>
                <li>
                  <a href="#services">React & Angular</a>
                </li>
                <li>
                  <a href="#services">Mobile Development</a>
                </li>
                <li>
                  <a href="#services">IT Consulting</a>
                </li>
              </ul>
            </div>
            <div className="footer-col">
              <h4 className="footer-title">Company</h4>
              <ul className="footer-links">
                <li>
                  <a href="#about">About Us</a>
                </li>
                <li>
                  <a href="#portfolio">Portfolio</a>
                </li>
                <li>
                  <a href="#testimonials">Testimonials</a>
                </li>
                <li>
                  <a href="#contact">Contact</a>
                </li>
              </ul>
            </div>
            <div className="footer-col">
              <h4 className="footer-title">Connect</h4>
              <ul className="footer-links">
                <li>
                  <Phone size={16} className="inline mr-2" />
                  +91 9902545635
                </li>
                <li>
                  <Mail size={16} className="inline mr-2" />
                  Info@threemsolutions.com
                </li>
                <li>
                  <MapPin size={16} className="inline mr-2" />
                  Bangalore, India
                </li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>
              © 2024 Threem Technology Solutions Pvt. Ltd. • Founded January 19,
              2024 • All Rights Reserved
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
